import { HeadData } from '@/components/HeadData';
import { type FragmentType, useFragment } from '@/gql/codegen';
import { graphql } from '@/gql/codegen/gql';
import { useSidebar } from '@/routes/_embaUserLayout/user-attendances/$userAttendanceSlug/_lessonSideBarLayout';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import Page from '../-Page';
import { useOverwriteGoalMutation } from '../-hooks/useOverwriteGoalMutation';
import { useEvaluationPolling } from '../../-hooks/useEvaluationPolling';
import { COURSE_LESSON_DETAIL_FRAGMENT } from '../../index.route.lazy';

export const GOAL_DIALOG_RESULT_FRAGMENT = graphql(/* GraphQL */ `
  fragment GoalDialogResult on UserGoalDialogResult {
    id
    isSummarized
    goal
    overwriteBlockReason
    embaCourse {
      name
    }
  }
`);

export type GoalDialogResultContainerProps = {
  goalDialogResult: FragmentType<typeof GOAL_DIALOG_RESULT_FRAGMENT>;
  userAttendanceSlug: string;
  lessonSlug: string;
  startPolling: (interval: number) => void;
  stopPolling: () => void;
  isTimeout: boolean;
  courseLesson: FragmentType<typeof COURSE_LESSON_DETAIL_FRAGMENT>;
};

export default function GoalDialogResultContainer({
  goalDialogResult,
  userAttendanceSlug,
  lessonSlug,
  startPolling,
  stopPolling,
  isTimeout,
  courseLesson,
}: GoalDialogResultContainerProps) {
  const { showSidebar } = useSidebar();
  useEffect(() => {
    showSidebar();
  }, [showSidebar]);
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedGoal, setEditedGoal] = useState('');
  const [isTimeoutModalOpen, setIsTimeoutModalOpen] = useState(isTimeout);

  const goalDialogResultData = useFragment(
    GOAL_DIALOG_RESULT_FRAGMENT,
    goalDialogResult,
  );

  const courseLessonData = useFragment(
    COURSE_LESSON_DETAIL_FRAGMENT,
    courseLesson,
  );

  useEvaluationPolling(
    goalDialogResultData.isSummarized,
    startPolling,
    stopPolling,
  );

  const {
    overwriteGoal,
    overwriteGoalMutationError,
    overwriteGoalMutationLoading,
  } = useOverwriteGoalMutation(userAttendanceSlug, lessonSlug);

  if (overwriteGoalMutationError) {
    throw overwriteGoalMutationError;
  }

  const onOpenModal = () => {
    setIsEditModalOpen(true);
    setEditedGoal(goalDialogResultData.goal || '');
  };

  const onCloseModal = () => {
    setIsEditModalOpen(false);
    setEditedGoal('');
  };

  const onOverwriteGoal = async () => {
    await overwriteGoal(editedGoal);
    setIsEditModalOpen(false);
    setEditedGoal('');
  };

  const onNext = () => {
    navigate({
      to: '/user-attendances/$userAttendanceSlug/lessons/$lessonSlug/complete',
      params: {
        userAttendanceSlug,
        lessonSlug,
      },
    });
  };

  const headTitle = goalDialogResultData.embaCourse.name;

  return (
    <>
      <HeadData title={headTitle} />
      <Page
        onOpenModal={onOpenModal}
        onCloseModal={onCloseModal}
        onOverwriteGoal={onOverwriteGoal}
        onNext={onNext}
        isSummarized={goalDialogResultData.isSummarized}
        isOverwriting={overwriteGoalMutationLoading}
        goal={goalDialogResultData.goal}
        isEditModalOpen={isEditModalOpen}
        editedGoal={editedGoal}
        setEditedGoal={setEditedGoal}
        isTimeoutModalOpen={isTimeoutModalOpen}
        setIsTimeoutModalOpen={setIsTimeoutModalOpen}
        overwriteBlockReason={goalDialogResultData.overwriteBlockReason}
        chapterName={courseLessonData.embaCourseChapter.name}
        lessonName={courseLessonData.name}
      />
    </>
  );
}
